import React, { useCallback, useEffect, useState } from 'react';
import { Container, Row, Stack } from 'react-bootstrap';
import { asve } from '../orve';
import {
  getTipContent,
  getConditionalExperienceTip,
  valuesRatingBreakpoints,
  getCandidateValuesText,
} from '../report-util';
import AcumenTips from './acument-tips';
import { useAuth } from '../../../../providers/auth-provider';
import { REPORT_TYPE } from '../../../../utils/constants';
import { getPercentileAttributesConstants } from '../../../../api/dashboards-api';
import { getExperienceTips, getExperienceTitle, getValuesBreakpoints, getValuesTips } from '../assessment-verbiage';

export const NEW_HIRE_VALUE_TIPS = 1;
export const INDIVIDUAL_VALUE_TIPS = 2;
export const LEADER_VALUE_TIPS = 3;

const MAX_BULLETS_PER_PAGE = 13;

const renderState = { acumenGroup: false, valuesGroup: false, experienceGroup: false };

const getNAcumenBullets = (candidate, withAcumen) => {
  if (withAcumen && candidate.percentiles.acumen < 66) {
    return 4;
  }

  return 0;
};

function AcumenValuesExperienceTips({
  candidate,
  topFourMisAligned = false,
  withAcumen = true,
  withValues = true,
  withExperience = true,
  singlePage = true,
  valuesTips = NEW_HIRE_VALUE_TIPS,
  reportType = REPORT_TYPE.coaching,
  remoteStaffing = false,
}) {
  const { isSelect } = useAuth();
  const [valuesFitTips, setValuesFitTips] = useState({});
  const [experienceTips, setExperienceTips] = useState({});
  const [initialized, setInitialized] = useState(false);
  const [showAcumen, setShowAcumen] = useState(false);
  const [showValues, setShowValues] = useState(false);
  const [showExperience, setShowExperience] = useState(false);

  const pageHasContent = useCallback(() => {
    return showAcumen || showExperience || showValues;
  }, [showAcumen, showExperience, showValues]);

  const getVariableValuesTipKeys = (candidate) => {
    const valuesKeys = [];
    for (const key of candidate.values.variableKeys) {
      const tips = getValuesTips(candidate, key);
      if (tips?.length > 0) {
        valuesKeys.push(key);
      }
    }

    return valuesKeys;
  };
  const getVariableExperienceTipKeys = (candidate) => {
    const valuesKeys = [];
    for (const key of candidate.experience.variableKeys) {
      const tips = getExperienceTips(candidate, key);
      if (tips?.length > 0) {
        valuesKeys.push(key);
      }
    }

    return valuesKeys;
  };
  const getValuesKeys = (candidate, topFourMisAligned) => {
    if (reportType !== REPORT_TYPE.staffing) {
      let keys = [...candidate.values.misAligned];
      if (topFourMisAligned) {
        keys = keys.slice(0, 4);
      }
      return keys;
    } else {
      return getVariableValuesTipKeys(candidate);
    }
  };

  const getNValuesBullets = (candidate, withValues, topFourMisAligned) => {
    if (withValues) {
      if (reportType !== REPORT_TYPE.staffing) {
        return getValuesKeys(candidate, topFourMisAligned).length;
      } else {
        return getVariableValuesTipKeys(candidate).length;
      }
    }

    return 0;
  };

  const getNExperienceBullets = (candidate, withExperience) => {
    let experienceAttribute = 'experience';
    if (reportType === REPORT_TYPE.staffing) {
      if (!remoteStaffing) {
        experienceAttribute = getPercentileAttributesConstants().onsite.experience;
      } else {
        experienceAttribute = getPercentileAttributesConstants().remote.experience;
      }
    }
    if (withExperience && candidate.percentiles[experienceAttribute] < 66) {
      let nBullets = 0;
      let keys;
      if (reportType !== REPORT_TYPE.staffing) {
        keys = Object.keys(candidate.experience.scores);
      } else {
        keys = getVariableExperienceTipKeys(candidate);
      }
      keys.map((key) => {
        if (candidate.experience.scores[key] < 4) {
          nBullets += 1;
        }
      });

      return nBullets;
    }

    return 0;
  };

  const getValuesFitTips = useCallback(() => {
    const tips = [];
    if (reportType !== REPORT_TYPE.staffing) {
      const keys = getValuesKeys(candidate, topFourMisAligned);
      keys.map((key) => {
        const score = candidate.values.ratings[key];
        const title = valuesFitTips[key].title;
        const description = valuesFitTips[key].text;
        tips.push(
          <Stack direction="horizontal" className="pt-0 pb-2" key={key}>
            <div>{getTipContent(title, score, description, valuesRatingBreakpoints, reportType)}</div>
          </Stack>
        );
      });
    } else {
      const keys = getVariableValuesTipKeys(candidate);
      const breakpoints = getValuesBreakpoints(candidate);
      keys.map((key) => {
        const score = candidate.values.scores[key];
        const tip = getValuesTips(candidate, key)[0];
        const title = tip.title;
        const description = tip.text;
        tips.push(
          <Stack direction="horizontal" className="pt-0 pb-2" key={key}>
            <div>{getTipContent(title, score, description, breakpoints, reportType)}</div>
          </Stack>
        );
      });
    }
    return tips;
  }, [valuesFitTips, candidate, topFourMisAligned]);

  useEffect(() => {
    if (!initialized && candidate) {
      switch (valuesTips) {
        case NEW_HIRE_VALUE_TIPS:
          setValuesFitTips(asve(candidate).newHireValuesFitTips);
          break;
        case INDIVIDUAL_VALUE_TIPS:
          setValuesFitTips(asve(candidate).individualValuesFitTips);
          break;
        case LEADER_VALUE_TIPS:
          setValuesFitTips(asve(candidate).leaderValuesFitTips);
          break;
      }
      if (reportType === REPORT_TYPE.development) {
        setExperienceTips(asve(candidate).developmentExperienceTips);
      } else {
        setExperienceTips(asve(candidate).experienceTips);
      }

      if (singlePage) {
        let nBullets = getNAcumenBullets(candidate, withAcumen);
        if (nBullets > 0) {
          setShowAcumen(true);
        }
        nBullets = getNValuesBullets(candidate, withValues, topFourMisAligned);
        if (nBullets > 0) {
          setShowValues(true);
        }
        nBullets = getNExperienceBullets(candidate, withExperience);
        if (nBullets > 0) {
          setShowExperience(true);
        }
      } else {
        let totalBullets = getNAcumenBullets(candidate, withAcumen);
        if (totalBullets > 0) {
          setShowAcumen(true);
        }
        if (!renderState.valuesGroup) {
          renderState.acumenGroup = true;
        }

        if (!renderState.valuesGroup && !renderState.experienceGroup) {
          const nValuesBullets = getNValuesBullets(candidate, withValues, topFourMisAligned);
          if (nValuesBullets > 0) {
            if (totalBullets === 0) {
              setShowValues(true);
              renderState.valuesGroup = true;
              totalBullets += nValuesBullets;
            } else if (totalBullets + nValuesBullets <= MAX_BULLETS_PER_PAGE) {
              setShowValues(true);
              renderState.valuesGroup = true;
              totalBullets += nValuesBullets;
            }
          }
        }

        if (!renderState.experienceGroup) {
          const nExperienceBullets = getNExperienceBullets(candidate, withExperience);
          if (nExperienceBullets > 0) {
            if (totalBullets === 0) {
              setShowExperience(true);
              renderState.experienceGroup = true;
            } else if (totalBullets + nExperienceBullets <= MAX_BULLETS_PER_PAGE) {
              setShowExperience(true);
              renderState.experienceGroup = true;
            }
          }
        }
      }

      setInitialized(true);
    }
  }, [candidate, valuesTips, withAcumen, withValues, withExperience, topFourMisAligned, singlePage]);

  return (
    <>
      {initialized && pageHasContent() && (
        <Container className="bg-white mb-3 candidate-profile-assessments">
          {showAcumen && <AcumenTips candidate={candidate} reportType={reportType} />}
          {showValues && (
            <>
              <h3 className="pt-4 pb-3">{getCandidateValuesText(isSelect(), true).toUpperCase()}</h3>
              <Row className="w-100 mx-auto justify-content-center pt-1 pb-3">{getValuesFitTips()}</Row>
            </>
          )}
          {showExperience && (
            <>
              <h3 className="pt-4 pb-3">EXPERIENCE</h3>
              <Row className="w-100 mx-auto justify-content-center pt-1">
                <>
                  {!candidate.experience.variableKeys ? (
                    <>
                      {Object.keys(candidate.experience.scores).map((key) => {
                        return getConditionalExperienceTip(
                          candidate.experience.scores[key],
                          experienceTips[key].title,
                          experienceTips[key].text,
                          reportType
                        );
                      })}
                    </>
                  ) : (
                    <>
                      {candidate.experience.variableKeys.map((key) => {
                        const title = getExperienceTitle(candidate, key);
                        if (title) {
                          const tip = getExperienceTips(candidate, key, reportType)[0];
                          return getConditionalExperienceTip(
                            candidate.experience.scores[key],
                            tip.title,
                            tip.text,
                            reportType
                          );
                        }
                      })}
                    </>
                  )}
                </>
              </Row>
            </>
          )}
        </Container>
      )}
    </>
  );
}

export default AcumenValuesExperienceTips;
